import { OpenImgContextProvider } from 'openimg/react'
import {
	type HeadersFunction,
	json,
	type LinksFunction,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { SpeedInsights } from '@vercel/speed-insights/remix'
import React, { useEffect } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import reactLightboxStyleSheetUrl from "yet-another-react-lightbox/styles.css?url"
import { usePostHog } from '#app/hooks/use-posthog.ts'
import fontStyleSheetUrl from '../public/fonts/stylesheet.css?url'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import masonryStyleSheetUrl from './styles/masonry.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import Preloader from './components/Preloader.tsx'
import usePreloader from './hooks/usePreloader.tsx'
import { LenisProvider } from './provider/lenis-provider.tsx'
import { defaultBreakpoints, defaultFormats, getSrc, setR2BucketUrl } from './utils/image-optimization.tsx'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: masonryStyleSheetUrl },
		{ rel: 'stylesheet', href: fontStyleSheetUrl },
		{ rel: 'stylesheet', href: reactLightboxStyleSheetUrl },
		{
			rel: 'stylesheet',
			href: 'https://cdn.jsdelivr.net/npm/swiper/swiper-bundle.min.css',
		},
	].filter(Boolean)
}

export const meta: MetaFunction = ({ data }) => {
	const metaData = {
		title: '7even Sports',
		description: '7even Sports is a league dedicated to sports enthusiasts in Ghana.',
		keywords: 'sports,ghana sports,7even sports,football,league,ghana football',
		image: 'https://i.imgur.com/bNo79cP.jpeg',
		url: 'https://7evensports.com',
		type: 'website'
	}

	return [
		{ title: data ? metaData.title : `Error | ${metaData.title}` },
		{ name: 'description', content: metaData.description },
		{ name: 'keywords', content: metaData.keywords },
		{ property: 'og:type', content: metaData.type },
		{ property: 'og:site_name', content: metaData.title },
		{ property: 'og:title', content: data ? metaData.title : `Error | ${metaData.title}` },
		{ property: 'og:description', content: metaData.description },
		{ property: 'og:url', content: metaData.url },
		{ property: 'og:image', content: metaData.image },
		{ property: 'og:image:width', content: '1200' },
		{ property: 'og:image:height', content: '630' },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:site', content: '@7evensports' },
		{ name: 'twitter:title', content: data ? metaData.title : `Error | ${metaData.title}` },
		{ name: 'twitter:description', content: metaData.description },
		{ name: 'twitter:image', content: metaData.image },
		{ name: 'theme-color', content: '#161616' },
		{ name: 'application-name', content: metaData.title },
		{ name: 'apple-mobile-web-app-capable', content: 'yes' },
		{ name: 'apple-mobile-web-app-status-bar-style', content: 'black' },
		{ name: 'apple-mobile-web-app-title', content: metaData.title },
		{ property: 'og:locale', content: 'en_GH' },
		{ 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' }
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
			() =>
				prisma.user.findUniqueOrThrow({
					select: {
						id: true,
						name: true,
						email: true,
						avatar: true,
						country: true,
						phoneNumber: true,
						runClubMember: {
							select: {
								id: true
							}
						},
						roles: {
							select: {
								role: {
									select: {
										name: true,
										permissions: {
											select: { entity: true, action: true, access: true },
										},
									},
								},
							},
						},
						team: {
							select: {
								id: true,
								name: true,
								slug: true,
								teamColor: true,
								homeStadium: true,
								country: true,
								foundedDate: true,
								logo: true,
							},
						},
						playerProfile: {
							select: {
								id: true,
								position: true,
								dateOfBirth: true,
								height: true,
								weight: true,
								team: {
									select: {
										slug: true,
									},
								},
							},
						},
						organization: {
							select: {
								slug: true,
								logo: true,
							},
						},
					},
					where: { id: userId },
				}),
			{ timings, type: 'find user', desc: 'find user in root' },
		)
		: null
	if (userId && !user) {
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	const env = getEnv()

	return json(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
			},
			ENV: {
				MODE: env.MODE,
				R2_PUBLIC_URL: env.R2_PUBLIC_URL || '',
			},
			toast,
			honeyProps,
			theme: await getTheme(request),
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	env?: Record<string, string>
}) {
	return (
		<html lang="en" className="h-full overflow-x-hidden font-sans bg-[#161616]">
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Links />
			</head>
			<body className='bg-[#161616]'>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const { isLoading, completeLoading } = usePreloader()
	const [isMounted, setIsMounted] = React.useState(false)

	useToast(data.toast)
	usePostHog()

	// Only run on client to prevent hydration mismatch
	React.useEffect(() => {
		setIsMounted(true)
	}, [])

	// Configure R2 bucket URL from environment
	React.useEffect(() => {
		if (data.ENV.R2_PUBLIC_URL) {
			setR2BucketUrl(data.ENV.R2_PUBLIC_URL)
		}
	}, [data.ENV.R2_PUBLIC_URL])

	return (
		<Document nonce={nonce} env={data.ENV}>
			<OpenImgContextProvider
				optimizerEndpoint="/api/image-optimizer"
				breakpoints={defaultBreakpoints}
				targetFormats={['webp', 'jpg'] as any[]}
				getSrc={getSrc}
			>
				{isMounted && isLoading && <Preloader onComplete={completeLoading} />}
				<LenisProvider>
					<div style={{ opacity: isMounted && isLoading ? 0 : 1, transition: 'opacity 500ms ease-in-out' }}>
						<Outlet />
					</div>
				</LenisProvider>
			</OpenImgContextProvider>
			<SpeedInsights />
			<EpicToaster closeButton position="bottom-right" />
			<EpicProgress />
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
